/*
 * Motion column headers and layer chips (W2-04a). Design tokens only; the host gives the header 52 px.
 * Width-adaptive (W3-05, UX-034): the header is a size container; below about 84 px the title uses the tiny names
 * (X, Y, XY, Rot, Op, Spd) and the X/Y link toggle stays in the column menu only, below 110 px the readout shows the
 * value alone (no "now", no Σ, the whole text in the tooltip) and badges keep only their icon and count, below
 * 170 px Scroll speed drops "crosses in".
 */

.header {
  container-type: inline-size;
  container-name: motion-header;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1px;
  height: 100%;
  padding: 2px 4px 2px 6px;
  background: var(--color-surface-1);
  border-bottom: var(--border-width, 1px) solid var(--color-border);
  border-top: 2px solid transparent;
  font-family: var(--font-sans);
  font-size: var(--text-2xs);
  color: var(--color-text-muted);
  overflow: hidden;
  user-select: none;
}

.header[data-focused='true'] {
  border-top-color: var(--color-accent);
  background: var(--color-accent-muted);
  color: var(--color-text);
}

.header[data-pinned='true'] {
  background: var(--color-surface-2);
}

.row {
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
  height: 16px;
}

.title {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  border-radius: var(--radius-xs);
}

.title:focus-visible {
  outline: var(--focus-ring-width, 2px) solid var(--color-focus-ring);
  outline-offset: 1px;
}

.titleTiny,
.nowNarrow,
.narrowOnly {
  display: none;
}

.nowWide,
.nowNarrow {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The active layer chip and its menu button stay together and never shrink under their neighbours. */
.chipGroup {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 1px;
}

@container motion-header (max-width: 83px) {
  .titleWide,
  .sub,
  .iconBtn.linkBtn {
    display: none;
  }

  .titleTiny {
    display: inline;
  }
}

@container motion-header (max-width: 109px) {
  .nowWide,
  .wideOnly,
  .sigma[data-kind='sigma'] {
    display: none;
  }

  .nowNarrow,
  .narrowOnly {
    display: inline;
  }

  /* The layer chip keeps the room: the speed-mode badges ("1.7 speed") show in wider columns only. */
  .chipRow:has(.chip) > .badge[data-tone='info'],
  .chipRow:has(.chip) > .badge[data-tone='baked'] {
    display: none;
  }
}

@container motion-header (max-width: 169px) {
  .sigma[data-kind='crosses'] {
    display: none;
  }
}

.sub {
  flex: 0 0 auto;
  color: var(--color-text-subtle);
  font-weight: var(--weight-normal);
  text-transform: none;
}

.iconBtn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-xs);
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
}

.iconBtn:hover {
  background: var(--color-surface-hover);
  color: var(--color-text);
}

.iconBtn:focus-visible {
  outline: var(--focus-ring-width, 2px) solid var(--color-focus-ring);
}

.iconBtn[aria-pressed='true'] {
  color: var(--color-warning);
}

.iconBtn[aria-disabled='true'] {
  opacity: 0.45;
  cursor: default;
}

.chipRow {
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
  height: 16px;
  overflow: hidden;
}

.chip {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 14px;
  padding: 0 4px;
  border: var(--border-width, 1px) solid var(--color-border);
  border-radius: var(--radius-pill);
  background: var(--color-surface-2);
  color: var(--color-text-muted);
  font: inherit;
  font-size: var(--text-2xs);
  line-height: 1;
  cursor: pointer;
}

.chip[aria-pressed='true'] {
  border-color: var(--color-accent-border);
  background: var(--color-accent);
  color: var(--color-text-on-accent);
}

.chip[data-muted='true'] {
  text-decoration: line-through;
  opacity: 0.75;
}

.chip:focus-visible {
  outline: var(--focus-ring-width, 2px) solid var(--color-focus-ring);
  outline-offset: 1px;
}

.dot {
  display: inline-block;
  width: 5px;
  height: 5px;
  border-radius: var(--radius-pill);
  border: 1px solid currentColor;
  box-sizing: border-box;
}

.dot[data-has='true'] {
  background: currentColor;
}

.chipInput {
  width: 64px;
  height: 14px;
  padding: 0 3px;
  border: var(--border-width, 1px) solid var(--color-accent-border);
  border-radius: var(--radius-xs);
  background: var(--color-surface-sunken);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-2xs);
}

.badge {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 14px;
  padding: 0 4px;
  border: 0;
  border-radius: var(--radius-xs);
  background: var(--color-surface-3);
  color: var(--color-text-muted);
  font: inherit;
  font-size: var(--text-2xs);
  white-space: nowrap;
}

button.badge {
  cursor: pointer;
}

.badge[data-tone='inUse'] {
  background: var(--color-badge-in-use-muted);
  color: var(--color-badge-in-use);
}

.badge[data-tone='baked'] {
  background: var(--color-badge-baked-muted);
  color: var(--color-badge-baked);
}

.badge[data-tone='info'] {
  background: var(--color-info-muted);
  color: var(--color-info);
}

.badge[data-tone='warning'] {
  background: var(--color-warning-muted);
  color: var(--color-warning);
}

/* The live readout repaints about 15 times a second: contained, so it does not repaint the page (UX-027). */
.now {
  contain: layout paint;
  display: flex;
  align-items: center;
  gap: 6px;
  height: 14px;
  font-family: var(--font-mono);
  font-size: var(--text-2xs);
  white-space: nowrap;
  overflow: hidden;
  color: var(--color-text-muted);
}

.sigma {
  color: var(--color-text-subtle);
}

@media (prefers-reduced-motion: reduce) {
  .chip,
  .iconBtn,
  .title {
    transition: none;
  }
}
